import { useCallback } from 'react';

import { State } from '@/types';

import { usePostFeaturedPeople } from '../api/post-featured-people';
import { TFeaturedPeopleType } from '../types';

type TDeclarantFields = {
  people_id?: string | null;
  entity_label?: string | null;
};

/**
 * Persists a declarant typed on the fly as a Featured People record, so it feeds
 * future dropdowns instead of staying orphaned behind a free-text `entity_label`.
 *
 * The record is created without images and with `active_detection: false`: face
 * detection has no reference picture to register, and the UI already falls back
 * to `/default-logo.svg` wherever a portrait is missing.
 */
export function useSaveDeclarantAsPeople() {
  const { mutateAsync: createPeople, isPending: isSaving } = usePostFeaturedPeople();

  const saveDeclarant = useCallback(
    async (name: string): Promise<TFeaturedPeopleType> => {
      const trimmedName = name.trim();

      if (!trimmedName) {
        throw new Error('A declarant name is required to save it as a featured people');
      }

      return createPeople({
        name: trimmedName,
        state: State.Inactive,
        active_detection: false,
        description: null,
        alias: null,
        main_logo: '',
        logos: [],
      });
    },
    [createPeople]
  );

  /**
   * Resolves a free-text declarant into a catalogue record when the user asked for
   * it, leaving the values untouched otherwise. Returns `null` when the record
   * could not be created, meaning the caller must persist nothing at all.
   */
  const linkDeclarant = useCallback(
    async <T extends TDeclarantFields>(data: T, shouldSave: boolean): Promise<T | null> => {
      if (!shouldSave || data.people_id || !data.entity_label?.trim()) return data;

      try {
        const people = await saveDeclarant(data.entity_label);
        return { ...data, people_id: people.id, entity_label: people.name };
      } catch {
        // usePostFeaturedPeople already notified the failure.
        return null;
      }
    },
    [saveDeclarant]
  );

  return { linkDeclarant, isSaving };
}
